import {
  Box,
  chakra,
  Checkbox,
  Flex,
  Icon,
  Image,
  shouldForwardProp,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { COMMON_WEB_PATH } from "../../constant";
import { useDispatch, useSelector } from "react-redux";
import { toggleBGM } from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import LoadingContainer from "../Loading/LoadingContainer";
import useSound from "../../hook/useSound";
import dayjs from "dayjs";
import { ImSpinner6 } from "react-icons/im";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import useLongMachineSetting from "../../hook/useLongMachineSetting";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const WAIT_TIME = 30;

/**
 * 棄分確認彈窗。
 *
 * ⚠️ loading 遮罩改吃父層的 `isProcessing`,不要再自己開一個 state。
 *
 * 原本這裡有個 `isClosing`,在 handleConfirm / handleCancel 各被設成 true 一次,
 * 而整份檔案沒有任何一處把它設回 false —— 它是靠「父層把 showConfirm 設成 false、
 * 讓整個元件卸載」來收掉遮罩的。問題是父層的 catch 只有 console.error,不關彈窗,
 * 所以 API 一失敗遮罩就永遠留著,而且 LoadingContainer 是滿版 absolute + zIndex 999,
 * 連下面的取消鈕都點不到,機台上只能重開。
 *
 * `isProcessing` 由父層用 try/finally 管理,成功失敗都會歸位,不會有這個問題。
 * 父層本來就有把它傳進來,只是這裡的 props 解構沒收,白傳了。
 */
const ClearCashConfirm = ({
  onConfirm,
  onCancel,
  isProcessing = false,
  setChecked,
  checked,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const [remainingTime, setRemainingTime] = useState("-");
  const [isLoading, setIsLoading] = useState(true);

  const settle_music = useSound({ customSound: "settle", volume: 0.5 });

  const isBGMEnabled = useSelector((state) => state.isBGMEnabled);
  const dispatch = useDispatch();
  const { images } = useWebsiteInfo();

  const memberData = useSelector((state) => state.isMemberInfo);
  const canTransferPoints = memberData?.open_trans_point === 1; //是否可以轉移點數

  const [showOverlay, setShowOverlay] = useState(true); // 是否顯示動畫
  const [key, setKey] = useState(0); // 動畫key

  // useEffect(() => {
  //   setKey((prev) => prev + 1); // 每次組件掛載時更新 key
  //   const timer = setTimeout(() => {
  //     setShowOverlay(false);
  //   }, 400);
  //   return () => clearTimeout(timer);
  // }, []);

  //處理是否轉移點數
  const handleCheck = (e) => {
    setChecked(e.target.checked);
  };

  // 處理確認按鈕
  const handleConfirm = () => {
    if (remainingTime !== "done" || isProcessing) return;
    onConfirm();
  };

  // 處理取消按鈕
  const handleCancel = () => {
    // 請求還在飛的時候不讓關 —— 關掉會讓成功回來時找不到彈窗可收,狀態會對不上
    if (isProcessing) return;
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        song: "back2",
        musicVolume: 0.2,
      })
    );
    onCancel();
  };

  useEffect(() => {
    dispatch(
      toggleBGM({
        ...isBGMEnabled,
        enabled: true,
        song: "settle",
        musicVolume: 0.5,
      })
    );
  }, []);

  useEffect(() => {
    const playedTime = localStorage.getItem("played_time");
    if (playedTime) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const playedTime = localStorage.getItem("played_time");
    const playedTimeDate = dayjs(playedTime);

    if (playedTime && dayjs(playedTime).isValid()) {
      const interval = setInterval(() => {
        const now = dayjs();
        const diff = now.diff(playedTimeDate, "second");
        if (diff <= WAIT_TIME) {
          if (diff === WAIT_TIME) {
            setRemainingTime("done");
          } else {
            setRemainingTime(WAIT_TIME - diff);
          }
        } else {
          clearInterval(interval);
          setRemainingTime("done");
          localStorage.removeItem("played_time");
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setRemainingTime("done");
    }
  }, []);

  const { longMachinePaddingTop } = useLongMachineSetting();
  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.8)"
      zIndex="9999"
      backdropFilter="blur(2px)"
      justifyContent="center"
      alignItems="center"
      pt={longMachinePaddingTop}
    >
      {/* <ChakraBox
        key={key}
        position="absolute"
        zIndex="10000"
        initial={{ opacity: 1 }}
        animate={{ opacity: showOverlay ? 1 : 0 }}
        transition={{ duration: 0.4 }} // 代表動畫持續時間
        display={showOverlay ? "block" : "none"}
      >
        <Image src={`${COMMON_WEB_PATH}/911TW/frame_show_rectangle_apng.png`} />
      </ChakraBox> */}

      <ChakraBox
        as={Flex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        w="680px"
        h="480px"
        position={"relative"}
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgImage: `url('${COMMON_WEB_PATH}/911TW/hintBG2.webp')`,
            bgPosition: "center",
            bgRepeat: "no-repeat",
            bgSize: "100% 100%",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgImage: `url('${COMMON_WEB_PATH}/911TW/getRealFrame.gif')`, //會動的框框gif
            bgPosition: "center",
            bgRepeat: "no-repeat",
            bgSize: "100% 100%",
            zIndex: 1,
            margin: "10px",
          },
        }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="space-between"
        p={6}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          position="relative"
          zIndex={2}
          w="100%"
          h="430px"
        >
          {isProcessing && <LoadingContainer />}
          <Flex
            fontSize="24px"
            color="white"
            textAlign="center"
            // bgImage={`url('${COMMON_WEB_PATH}/911TW/hintTitleBG.webp')`}
            // bgPosition="center"
            // bgRepeat="no-repeat"
            // bgSize="100% 100%"
            sx={{ width: "-webkit-fill-available" }}
            justifyContent="center"
            alignItems="center"
            h={"130px"}
          >
            <Image
              src={`${COMMON_WEB_PATH}/911TW/warning.webp`}
              height={{
                base: "50px",
                xl: "68px",
              }}
            />
          </Flex>
          {isLoading ? (
            <Flex alignItems={"center"} justifyContent={"center"}>
              <Icon
                as={ImSpinner6}
                fontSize={"30px"}
                className="animate-spin"
                color="yellow.500"
              />
            </Flex>
          ) : (
            <Flex
              w="100%"
              h={"150px"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box
                fontSize="48px"
                fontWeight="black"
                color="white"
                textAlign="center"
                sx={{
                  background:
                    "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
                }}
              >
                {remainingTime == "done"
                  ? i18n("confirm-clearCash")
                  : i18n("please-wait-game-settle")}
              </Box>

              {remainingTime !== "done" && (
                <Box
                  fontSize="24px"
                  color="white"
                  textAlign="center"
                  mb={2}
                  mt={-2}
                >
                  {i18n("remaining-time")}: {remainingTime} {i18n("seconds")}
                </Box>
              )}
            </Flex>
          )}
          <Flex
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            h={"130px"}
          >
            {/* {canTransferPoints && (
              //open_trans_point如果是1，才可以選擇點數移轉
              <Checkbox
                colorScheme="yellow"
                size="lg"
                onChange={handleCheck}
                checked={checked}
                sx={{
                  background:
                    "linear-gradient(180deg, #fff 0%,#fdd26a 60%,  #b07f20 80%,  #b07f20 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  textShadow: "0px 1px 3px rgba(255, 215, 0, 0.4)",
                  fontSize: "20px",
                  fontWeight: 700,
                }}
              >
                {i18n("goPointTransfer")}
              </Checkbox>
            )} */}
            <Flex
              gap={4}
              mt={3}
              mb={3}
              justifyContent="center"
              alignItems="center"
            >
              <Flex
                as="button"
                w="180px"
                h="75px"
                bgImage={`url('${images.yesBTN}')`}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                justifyContent="center"
                alignItems="center"
                filter={remainingTime == "done" ? "" : "grayscale(100%)"}
                onClick={handleConfirm}
                _hover={{
                  filter:
                    remainingTime == "done"
                      ? "brightness(1.1)"
                      : "grayscale(100%)",
                }}
                cursor={remainingTime == "done" ? "pointer" : "not-allowed"}
              >
                {/* <Image
              src={`${COMMON_WEB_PATH}/FlyDragon/yesWORD.webp`}
              width="70px"
              height={{
                base: "30px",
                xl: "30px",
              }}
            /> */}
              </Flex>

              <Flex
                as="button"
                w="180px"
                h="75px"
                bgImage={`url('${images.cancelBTN}')`}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                cursor={isProcessing ? "not-allowed" : "pointer"}
                justifyContent="center"
                alignItems="center"
                onClick={handleCancel}
                _hover={{ filter: isProcessing ? "" : "brightness(1.1)" }}
              >
                {/* <Image
              src={`${COMMON_WEB_PATH}/FlyDragon/cancelWORD.webp`}
              width="70px"
              height={{
                base: "30px",
                xl: "30px",
              }}
            /> */}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ChakraBox>
    </Flex>
  );
};

export default ClearCashConfirm;
